import React, { useEffect, useState, Fragment } from 'react';
import { format } from 'date-fns';

import { LoadingSpinner } from '@nerdwallet/currency/components/LoadingSpinner';
import { colors } from '@nerdwallet/base-styles/variables';
import { NDS_DATE_FORMAT } from '@constants/dates';

import { dashboardChartTheme } from './theme';

import styles from './dashboard-chart.module.scss';
import DashboardChartFlyout from './dashboard-chart-flyout';

let VictoryArea;
let VictoryAxis;
let VictoryChart;
let VictoryLabel;
let VictoryLine;
let VictoryPortal;
let VictoryScatter;
let VictoryVoronoiContainer;

export interface Props {
  data: any[];
  width?: number;
  height?: number;
  xAxisTicks: any[];
  yAxisTicks: any[];
  formatYTick?: any;
  formatFlyoutValue?: any;
  formatFlyoutSubtext?(any): any;
  disabled?: boolean;
  onLabelBecomeActive?(any): void;
  title?: string;
}

const DashboardChart = ({
  width = 560,
  height = 280,
  data,
  xAxisTicks,
  yAxisTicks,
  formatYTick = (val) => val,
  formatFlyoutValue = (val) => val,
  formatFlyoutSubtext = (val) => val,
  disabled,
  title,
  onLabelBecomeActive = (val) => val,
}: Props): JSX.Element => {
  const [isVictoryLoaded, setIsVictoryLoaded] = useState(false);
  useEffect(() => {
    import('victory').then((victory) => {
      ({
        VictoryArea,
        VictoryAxis,
        VictoryChart,
        VictoryLabel,
        VictoryLine,
        VictoryPortal,
        VictoryScatter,
        VictoryVoronoiContainer,
      } = victory);

      setIsVictoryLoaded(true);
    });
  }, []);

  if (!isVictoryLoaded) {
    return (
      <div className={styles.spinner}>
        <LoadingSpinner />
      </div>
    );
  }

  // use y-axis median to determine tooltip orientation
  const yValueMedian = yAxisTicks[Math.round((yAxisTicks.length - 1) / 2)];

  return (
    <Fragment>
      <svg style={{ height: 0, width: 0, display: 'block' }}>
        <defs>
          <linearGradient
            id="creditHistoryGradient"
            x1="0"
            x2="0"
            y1="0"
            y2="100%"
          >
            <stop
              offset={0}
              stopColor={disabled ? colors['neutral-light'] : colors.blue}
              stopOpacity={0.4}
            />
            <stop
              offset={1}
              stopColor={
                disabled ? colors['neutral-lightest'] : colors['blue-lightest']
              }
              stopOpacity={0}
            />
          </linearGradient>
        </defs>
      </svg>

      <VictoryChart
        theme={dashboardChartTheme}
        width={width}
        height={height}
        scale={{ x: 'time' }} // set x axis to support dates
        domainPadding={{ y: 2 }} // add padding to y axis to prevent grid lines from being cut off
        containerComponent={
          <VictoryVoronoiContainer
            className={styles.shadowChartContainer}
            voronoiBlacklist={[
              ...yAxisTicks.map((tick) => `${tick}BackgroundLine`),
              'creditHistoryLine',
              'creditHistoryArea',
            ]}
            title={title}
          />
        }
      >
        {/* Axis */}
        <VictoryAxis
          tickValues={xAxisTicks}
          tickLabelComponent={
            <VictoryLabel
              text={({ datum: tick }) => format(tick, NDS_DATE_FORMAT)}
              dx={({ datum: t }) => {
                // shift first and last labels so they align with graph edges
                switch (t) {
                  case xAxisTicks[0]:
                    return 36;
                  case xAxisTicks[xAxisTicks.length - 1]:
                    return -36;
                  default:
                    return 0;
                }
              }}
              dy={16}
            />
          }
        />

        <VictoryAxis
          dependentAxis
          tickCount={yAxisTicks.length}
          tickValues={yAxisTicks}
          tickLabelComponent={
            <VictoryLabel
              dx={-24}
              text={({ datum: tick }) => formatYTick(tick)}
            />
          }
        />

        {/* Background y-axis lines */}
        {yAxisTicks.map((yTick) => (
          <VictoryLine
            key={yTick}
            name={`${yTick}BackgroundLine`}
            data={xAxisTicks.map((xTick) => ({ x: xTick, y: yTick }))}
          />
        ))}

        {/* Data line, scatter points, and gradient below line */}
        <VictoryLine
          name="creditHistoryLine"
          data={data}
          style={{
            data: {
              stroke: disabled ? colors['neutral-light'] : colors.blue,
              strokeWidth: 4,
            },
          }}
        />

        <VictoryScatter
          theme={dashboardChartTheme}
          data={data}
          size={({ active }) => {
            if (active) return 6;
            // only show non-active ticks when there aren't too many data points
            if (data.length < 20) return 4;
            return 0;
          }}
          style={{
            data: {
              fill: disabled ? colors['neutral-light'] : colors.blue,
            },
          }}
          labels={({ datum: x }) => x}
          labelComponent={
            <VictoryPortal>
              <DashboardChartFlyout
                formatValue={formatFlyoutValue}
                formatSubtext={formatFlyoutSubtext}
                yValueMedian={yValueMedian}
                width={width}
                onBecomeActive={onLabelBecomeActive}
                x={0}
                y={0}
                datum={undefined}
                active={false}
              />
            </VictoryPortal>
          }
        />

        <VictoryArea
          name="creditHistoryArea"
          data={data.map(({ x, y }) => ({ x, y, y0: yAxisTicks[0] }))}
          style={{
            data: {
              fill: 'url(#creditHistoryGradient)',
            },
            stroke: 'none',
          }}
        />
      </VictoryChart>
    </Fragment>
  );
};

export default DashboardChart;
